import { API } from "@swivl-mono/api-helper";
import { IsTruthy } from "@swivl-mono/helpers";
import { BullCampaignUserJobData, BullJobType, BullQueueJobsBody, BullQueueName, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { SwivlException } from "../../../Helpers/Sentry/Sentry";

import LocationsModel from "../../../Models/Locations/Locations.Model";
import ModalModel from "../../../Models/Modal/Modal.Model";
import TenantsModel from "../../../Models/Tenants/Tenants.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { QuestsDropdown } from "../../Dropdowns/QuestsDropdown/QuestsDropdown";
import { SelectableList } from "../../SelectableList/SelectableList.Component";
import "./BroadcastModal.scss";
import { Member } from "../../../Models/Member/Member.Current";

interface ListItem {
  value: string;
  title: string;
  hoverText?: string;
  data?: any;
  disabled?: boolean;
  onClick?: () => void;
}

export const BroadcastModal = (props) => {
  // const { allVariables } = LocationVarsModel.useState((s) => s);
  const { broadcastTenants, isBroadcastTenantsLoading } = TenantsModel.useState((s) => s);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [messageText, setMessageText] = useState("");
  const [step, setStep] = useState(0);
  const [selectedQuest, setSelectedQuest] = useState<string>("after_broadcast");
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [isSendingUsersToProcess, setIsSendingUsersToProcess] = useState(false);
  const { locationsWithVariables } = LocationsModel.useState((s) => s);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    LocationVarsModel.loadAllVariables(false);
  }, [])

  const formattedTenants = useMemo(() => {
    const formattedTenants: ListItem[] =
      broadcastTenants?.map((tenant) => ({
        value: tenant.id.toString(),
        title: tenant.name || "Unknown Tenant",
        hoverText: tenant.phone || undefined,
        disabled: tenant.smsOptIn === false,
      })) || [];

    return formattedTenants;
  }, [broadcastTenants]);

  const formattedLocations = useMemo(() => {
    if (!locationsWithVariables || Object.keys(locationsWithVariables).length === 0) {
      return [];
    }
    return Object.values(locationsWithVariables)
      .filter(({ variables }) => {
        return IsTruthy(variables[LocationVarKey.HasSMSCampaigns]?.value)
      }).map(({ location, variables }) => ({
        value: String(location.id),
        title: variables[LocationVarKey.Name]?.value || "Unknown Location",
        hoverText: (variables[LocationVarKey.LocationNumber])?.value || "",
        data: variables,
      }))
  }, [locationsWithVariables]);

  const isLocationsLoading = !formattedLocations || formattedLocations?.length === 0;

  useEffect(() => {
    if (step === 1 && selectedLocationIds?.length) {
      TenantsModel.getUniqueBroadcastTenantsBy(selectedLocationIds, { active: true }, true);
    }
  }, [step]);

  const handleOnSendProcessUsers = async () => {
    setIsSendingUsersToProcess(true);

    const selectedUsers = broadcastTenants
      ?.filter((tenant) => selectedTenantIds?.includes(Number(tenant.id)))
      .flatMap((tenant) => {
        const users = Array.isArray(tenant.user) ? tenant.user : [tenant.user];
        return users.map((user) => ({
          user,
          locationId: tenant.locationId,
        }));
      })
      .filter(Boolean);

    const uniqueUsersList = Array.from(
      new Map(selectedUsers?.map((userObject) => [userObject.user.id, { user: userObject.user, locationId: userObject.locationId }])).values(),
    );

    const jobs: { name: string; data: BullCampaignUserJobData; options: any }[] =
      uniqueUsersList.map((userLocationObject) => {
        return {
          name: BullJobType.CampaignUsers,
          data: {
            user: userLocationObject.user,
            messageText: messageText,
            questSlug: selectedQuest,
            agentMemberId: Member?.id,
            locationId: userLocationObject.locationId,
          },
          options: {
            removeOnComplete: {
              age: 172800, // 2 days
            },
          },
        };
      }) || [];

    const bullQueueJobsBody: BullQueueJobsBody = [
      {
        queueName: BullQueueName.CampaignUsers,
        jobs: jobs,
      },
    ];

    try {
      await API.Bull.queueJobs(bullQueueJobsBody);
    } catch (error) {
      const errorToCapture = error instanceof Error ? error : new Error(String(error));
      SwivlException.capture(errorToCapture, "BroadcastModal.handleOnSendProcessUsers");
      throw error;
    }

    setIsSendingUsersToProcess(false);
    // NotificationsModel.displayNotification("Sending Messages", null, null, null, null, true, null, null);
    handleOnCancel();
  };

  const handleOnCancel = () => {
    ModalModel.hideModal();
  };

  const handleLocationItemSelected = (value: string) => {
    const selectedLocations: number[] = value?.length ? value.split(",").map(Number) : [];
    setSelectedLocationIds(selectedLocations);
  };

  const handleTenantItemSelected = (value: string) => {
    const selectedTenants: number[] = value?.length ? value.split(",").map(Number) : [];
    setSelectedTenantIds(selectedTenants);
  };

  const handleOnBack = () => {
    if (step === 1) {
      clearSelectedTenantIds();
    }
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleOnNext = () => {
    setStep(step + 1);
  };

  const clearSelectedTenantIds = () => {
    setSelectedTenantIds([]);
  };

  return (
    <div className="BroadcastModal">
      {step === 0 && (
        <>
          <label>SELECT LOCATION(S)</label>
          <div className="BroadcastStepContainer">
            <SelectableList
              open={true}
              title="Select Locations"
              openHeight={300}
              items={formattedLocations}
              selectedItemValue={selectedLocationIds.join(",")}
              onItemSelected={handleLocationItemSelected}
              showCheckboxes={true}
              showSelectAll={true}
              isLoading={isLocationsLoading}
              loadingText1={"Loading Locations..."}
              showSearch={true}
              useWindowing={true}
              windowHeight={200}
              windowWidth={477}
              windowItemHeight={26}
            />
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <label>SELECT CUSTOMER(S)</label>
          <div className="BroadcastStepContainer">
            <SelectableList
              open={true}
              title="Select Users"
              openHeight={300}
              items={formattedTenants || undefined}
              selectedItemValue={selectedTenantIds.join(",")}
              onItemSelected={handleTenantItemSelected}
              showCheckboxes={true}
              showSelectAll={true}
              isLoading={isBroadcastTenantsLoading}
              loadingText1={"Loading Customer(s)..."}
              showSearch={true}
              useWindowing={true}
              windowHeight={200}
              windowWidth={477}
              windowItemHeight={26}
            />
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="BroadcastStepContainer">
            <div className="BroadcastTextArea">
              <label>MESSAGE</label>
              <textarea
                rows={4}
                value={messageText}
                onChange={(e: any) => {
                  if (getMessageLength(e.target.value) <= 240) {
                    setMessageText(e.target.value);
                  }
                }}
                draggable={false}
              />
              <span className={`${getMessageLength(messageText) >= 240 ? "textMax" : ""}`.trim()}>{getMessageLength(messageText)}/240</span>
            </div>
            <label>FOLLOW ON ACTION</label>
            <QuestsDropdown
              name="questSlug"
              value={selectedQuest}
              questSelected={(slug) => {
                setSelectedQuest(slug);
              }}
              //Broadcast Messaging journey: 1.7, 2.1, test-journey
              filterJourney={(journey) => journey?.slug === "YZPFA4Xv2I" || journey?.slug === "k43o9hJGTB" || journey?.slug === "gSnWZZPMB9"}
            />
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className="BroadcastStepContainer">
            <div className="BroadcastPreview">
              <div className="previewTitle">Here's what you're sending:</div>
              <div className="message">
                <div className="messageTitle">Message:</div>
                <div className="messageContent">{messageText}</div>
              </div>
              <div className="audience">
                <div className="audienceTitle">Audience:</div>
                <div className="audienceContent">{`${selectedTenantIds?.length} customer(s) across ${selectedLocationIds?.length} location(s)`}</div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="footer">
        <div className="buttons">
          {step > 0 && (
            <Button variant={ButtonVariant.GHOST} onClick={handleOnBack}>
              Back
            </Button>
          )}

          {step == 0 && (
            <>
              <Button variant={ButtonVariant.GHOST} onClick={handleOnCancel}>
                Cancel
              </Button>

              <Button color={ButtonColor.WHITE} onClick={handleOnNext} disabled={selectedLocationIds.length === 0}>
                Next
              </Button>
            </>
          )}

          {step == 1 && (
            <Button color={ButtonColor.ORANGE} onClick={handleOnNext} disabled={selectedTenantIds.length === 0}>
              Next
            </Button>
          )}

          {step == 2 && (
            <Button color={ButtonColor.ORANGE} onClick={handleOnNext} disabled={!selectedQuest || !messageText}>
              Next
            </Button>
          )}

          {step === 3 && (
            <Button
              color={ButtonColor.ORANGE}
              onClick={handleOnSendProcessUsers}
              disabled={!messageText || !selectedQuest || !selectedTenantIds}
              loading={isSendingUsersToProcess}
            >
              Send Now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const getMessageLength = (message: string) => {
  let length = 0;
  for (let i = 0; i < message.length; i++) {
    let charCode = message.charCodeAt(i);
    if (0xd800 <= charCode && charCode <= 0xdbff) {
      // High surrogate of a surrogate pair.
      // Check if we have the next character and if it's a low surrogate.
      if (i + 1 < message.length) {
        let nextCharCode = message.charCodeAt(i + 1);
        if (0xdc00 <= nextCharCode && nextCharCode <= 0xdfff) {
          // It's a surrogate pair, count it as 2 and skip the next character.
          length += 2;
          i++;
          continue;
        }
      }
    }
    // For other characters, check if it's non-GSM
    if (charCode > 127) {
      length += 2;
    } else {
      length += 1;
    }
  }
  return length;
};
